type Singleton = {
  _instance?: ServerClientTime;
};

const getInitialDifference = () => {
  if (window.Aedu && Number.isInteger(window.Aedu.timeDifference)) {
    return window.Aedu.timeDifference as number;
  }
  return 0;
};

const getInitialFetchTime = () => {
  const defaultTime = new Date().getTime();
  if (typeof window?.Aedu?.serverRenderTime?.getTime !== 'function') {
    return defaultTime;
  }
  const initialFetchTime = window.Aedu.serverRenderTime.getTime();
  if (!Number.isInteger(initialFetchTime)) {
    return defaultTime;
  }
  return initialFetchTime;
};

/**
 * Interval to debounce synchronization updates by. Set to 5 minutes.
 *
 * This will minimize the chance of any client side time adjustments
 * affecting the order of impressions.
 */
const updateDebounceInterval = 5 * 60 * 1000;
/**
 * Threshold for the duration the client is lagging behind the server.
 * Set to 5 minutes.
 *
 * The server side validation checks that the lag time is under 10
 * minutes, this threshold needs to be below that to avoid getting
 * `impression_time_too_old` errors.
 */
const lagThreshold = -5 * 60 * 1000;
/**
 * Threshold for the duration the client is ahead of the server.
 * Set to 5 seconds.
 *
 * The server side validation checks that the lead time is under 10
 * seconds, this threshold needs to be below that to avoid getting
 * `impression_time_in_future` errors.
 */
const leadThreshold = 5 * 1000;

class ServerClientTime {
  /**
   * Difference between the client's local machine time and the server's
   * time in milliseconds.
   *
   * Computed by subtracting the server's time from the client's time
   * during synchronization.
   *
   * The server's time can be recomputed at any time by subtracting this
   * difference from the client's time, e.g.
   * `Date.now() - new ServerClientTime().difference`.
   */
  difference = getInitialDifference();
  /**
   * The last time the client computed the time difference.
   */
  lastFetch = getInitialFetchTime();

  /**
   * Singleton that keeps track of the time difference between the
   * client's local machine time and the server's time.
   *
   * @returns The singleton instance.
   */
  constructor() {
    const singleton = ServerClientTime as Singleton;
    if (singleton._instance) {
      return singleton._instance;
    }
    singleton._instance = this;
  }

  /**
   * Update the time difference between the client and server.
   *
   * The time difference is calculated by comparing the server's time
   * to the client's time. This method should be called whenever a
   * response is received from the server.
   *
   * @param response - The response from the server. This should contain
   * a `Date` header with the server's current time.
   */
  update = (response: Response) => {
    const serverDate = new Date(response.headers.get('Date') as string);
    const serverTime = serverDate.getTime();
    if (isNaN(serverTime)) {
      return;
    }

    const currentTime = new Date().getTime();
    const adjustedTime = currentTime - this.difference;
    const furtherDifference = adjustedTime - serverTime;

    if (
      // Difference threshold exceeded after existing adjustments
      furtherDifference < lagThreshold ||
      furtherDifference > leadThreshold ||
      // Debounce interval exceeded
      this.lastFetch < currentTime - updateDebounceInterval
    ) {
      this.difference = currentTime - serverTime;
      this.lastFetch = currentTime;
    }
  };
}

export default ServerClientTime;
